import type { ProjectDecodeLimits } from './ProjectDecodeLimits';
import type { ProjectLoadReport } from '@/utils/projectLoadProgress';
import {
  ChipModelDtoSchema,
  ProjectEnvelopeDtoSchema,
  assertProjectDefinitionReferences,
  type ProjectDto,
} from '@/schemas/chip';
import { jsonBlob } from '@/utils/jsonBlob';
import {
  COMPRESSED_PROJECT_MAGICS,
  compressProjectContainer,
  decompressProjectContainer,
} from './ProjectCompression';
import {
  SnapshotBlock,
  SNAPSHOT_BLOB_BATCH_BYTES,
  SNAPSHOT_BLOB_BATCH_RECORDS,
  SNAPSHOT_CAPTURE_BUDGETS,
  validateSnapshotLayout,
  type SnapshotPacket,
} from './SnapshotBlocks';
import { decodeStateValue, encodeStateValue } from './StateValue';
import { packTiles, unpackTiles, type PackedTiles } from './PackedTiles';
import {
  packConnections,
  unpackConnections,
  type PackedConnections,
} from './PackedConnections';
import {
  packRuntimeRecords,
  unpackRuntimeRecords,
  validatePackedRuntimeLayout,
  type PackedRuntimeGraph,
} from './PackedRuntimeRecords';
import {
  assertMetadataScalars,
  createMetadataScalarRestorer,
  metadataScalars,
  restoreMetadataScalars,
  type MetadataScalar,
} from './MetadataScalars';
import {
  packMetadataScalars,
  unpackMetadataScalars,
  type PackedMetadataScalars,
} from './PackedMetadataScalars';
import {
  projectBlockRoots,
  projectSnapshotData,
  projectSnapshotDto,
  receiveProjectSnapshotData,
  snapshotFromDto,
  type ProjectSnapshot,
  type ProjectSnapshotData,
  validateProjectSnapshot,
  validateProjectSnapshotLayout,
} from './ProjectSnapshot';

export const CURRENT_PROJECT_MIME = 'application/vnd.digitiles.compact-project';
export const CURRENT_PROJECT_EXTENSION = '.digitiles';
export const CURRENT_PROJECT_VERSION = 6;
export type ProjectFormat = 'current' | 'legacy-json' | 'unsupported';
const MAGIC = 'DGTLCMP6',
  PREVIOUS_MAGICS = ['DGTLCMP2', 'DGTLCMP3', 'DGTLCMP4', 'DGTLCMP5'],
  HEADER_BYTES = 16,
  MAX_MANIFEST_BYTES = 64 * 1024 * 1024;
export { MAX_MANIFEST_BYTES as MAX_PROJECT_MANIFEST_BYTES };
const MAX_BLOCK_BYTES = SNAPSHOT_CAPTURE_BUDGETS.bytes,
  MAX_BLOCKS = SNAPSHOT_CAPTURE_BUDGETS.records;
type Manifest = {
  version: 2 | 3 | 4 | 5 | 6;
  project: ProjectSnapshotData;
  roots: number[];
  records: { bytes: number; dependencies: number[] }[];
  metadataScalars?: MetadataScalar[] | PackedMetadataScalars;
  runtime?: { graphs: PackedRuntimeGraph[]; roots: number[] };
  tiles?: PackedTiles;
  connections?: PackedConnections;
};

/** Header detection only. Parsing/conversion of old JSON lives in a separate bundle. */
export async function inspectProjectFormat(raw: unknown): Promise<ProjectFormat> {
  if (raw instanceof Blob) {
    const prefix = new TextDecoder().decode(await raw.slice(0, 256).arrayBuffer());
    if (
      [MAGIC, ...COMPRESSED_PROJECT_MAGICS, ...PREVIOUS_MAGICS].some((magic) =>
        prefix.startsWith(magic),
      )
    )
      return 'current';
    if (/^\s*\{/.test(prefix)) return 'legacy-json';
    return 'unsupported';
  }
  return raw &&
    typeof raw === 'object' &&
    'name' in raw &&
    'chips' in raw &&
    'mainChipId' in raw
    ? 'legacy-json'
    : 'unsupported';
}

/** Only untyped state fields need tagged values. Schema-defined metadata stays
 * native JSON and does not get expanded into a parallel tagged object tree. */
function mapStates(
  project: ProjectSnapshotData,
  map: (state: unknown) => unknown,
): ProjectSnapshotData {
  const instance = (
    value: ProjectSnapshotData['chips'][number]['editingInstanceDto'],
  ) => ({
    ...value,
    logicGraphDto: {
      ...value.logicGraphDto,
      nodes: value.logicGraphDto.nodes.map((segment) =>
        'overflow' in segment
          ? {
              overflow: segment.overflow.map((node) => map(node) as typeof node),
            }
          : segment,
      ),
    },
  });
  const circuit = (value: ProjectDto['chips'][number]['logicCircuitDto']) => ({
    ...value,
    tileManagerDto: {
      ...value.tileManagerDto,
      tiles: value.tileManagerDto.tiles.map((tile) => ({
        ...tile,
        state: map(tile.state),
      })),
    },
  });
  return {
    ...project,
    chips: project.chips.map((chip) => ({
      ...chip,
      logicCircuitDto: circuit(chip.logicCircuitDto),
      structureCircuitDto: circuit(chip.structureCircuitDto),
      chipInstanceDtos: chip.chipInstanceDtos.map(instance),
      editingInstanceDto: instance(chip.editingInstanceDto),
    })),
  };
}

/** Write owned columns directly. No node DTO arrays, giant runtime-key strings,
 * permanent JSON copy, or consuming transfer of retained worker baselines. */
export async function encodeProjectSnapshot(
  project: ProjectSnapshot,
  options: { compression?: boolean } = {},
): Promise<Blob> {
  validateProjectSnapshot(project);
  const roots = projectBlockRoots(project),
    blocks: SnapshotBlock[] = [],
    indexes = new Map<SnapshotBlock, number>();
  let bytes = 0,
    references = 0;
  const visit = (block: SnapshotBlock): number => {
    const prior = indexes.get(block);
    if (prior !== undefined) return prior;
    if (
      blocks.length >= MAX_BLOCKS ||
      (bytes += block.byteLength) > MAX_BLOCK_BYTES ||
      references + block.dependencies.length > SNAPSHOT_CAPTURE_BUDGETS.references
    )
      throw new RangeError('Current project block budget exceeded');
    const index = blocks.length;
    indexes.set(block, index);
    blocks.push(block);
    references += block.dependencies.length;
    for (const dependency of block.dependencies) visit(dependency);
    return index;
  };
  const encodedStates = new WeakSet<object>();
  // Node and runtime blocks share the file's limits. Reserve the already owned
  // node blocks before compact runtime packing chooses blocks or overflow.
  const nodeRoots = roots.map(visit);
  const runtime = await packRuntimeRecords(
    projectSnapshotData(project, new Map(roots.map((root, i) => [root, i]))),
    {
      blocks: {
        bytes: MAX_BLOCK_BYTES - bytes,
        records: MAX_BLOCKS - blocks.length,
        references: SNAPSHOT_CAPTURE_BUDGETS.references - references,
      },
    },
  );
  const manifest: Manifest = {
    version: CURRENT_PROJECT_VERSION,
    project: mapStates(
      validateMetadata(runtime.project, true),
      (() => {
        const budget = { members: 0, text: 0 };
        return (state: unknown) => {
          const encoded = encodeStateValue(state, budget);
          if (encoded && typeof encoded === 'object') encodedStates.add(encoded);
          return encoded;
        };
      })(),
    ),
    roots: nodeRoots,
    runtime: { graphs: runtime.graphs, roots: runtime.roots.map(visit) },
    records: blocks.map((block) => ({
      bytes: block.byteLength,
      dependencies: block.dependencies.map((atom) => indexes.get(atom)!),
    })),
  };
  validateSnapshotLayout({
    roots: [...manifest.roots, ...manifest.runtime!.roots],
    records: manifest.records,
  });
  manifest.metadataScalars = packMetadataScalars(
    metadataScalars(manifest.project, encodedStates),
  );
  const packed = packTiles(manifest.project);
  const wiring = packConnections(packed.project, packed.tiles.strings);
  manifest.project = wiring.project;
  manifest.tiles = packed.tiles;
  manifest.connections = wiring.connections;
  const metadata = await jsonBlob(manifest, {
    pretty: false,
    strict: true,
    maxBytes: MAX_MANIFEST_BYTES,
  });
  if (metadata.size > MAX_MANIFEST_BYTES)
    throw new RangeError('Current project manifest budget exceeded');
  const header = new ArrayBuffer(HEADER_BYTES);
  new Uint8Array(header).set(new TextEncoder().encode(MAGIC));
  new DataView(header).setUint32(8, metadata.size, true);
  const parts: BlobPart[] = [header, metadata];
  let started = performance.now();
  let batch: SnapshotBlock[] = [],
    batchBytes = 0;
  const flush = () => {
    parts.push(SnapshotBlock.toBlobBatch(batch));
    batch = [];
    batchBytes = 0;
  };
  for (const block of blocks) {
    if (
      batch.length &&
      (batchBytes + block.byteLength > SNAPSHOT_BLOB_BATCH_BYTES ||
        batch.length >= SNAPSHOT_BLOB_BATCH_RECORDS)
    ) {
      flush();
      if (performance.now() - started >= 8) {
        await new Promise<void>((resolve) => setTimeout(resolve, 0));
        started = performance.now();
      }
    }
    batch.push(block);
    batchBytes += block.byteLength;
  }
  if (batch.length) {
    flush();
    if (performance.now() - started >= 8) {
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
    }
  }
  const raw = new Blob(parts, { type: CURRENT_PROJECT_MIME });
  return options.compression === false ? raw : compressProjectContainer(raw);
}

/** Shared converter/hydration boundary. Capture precedes the first async yield. */
export function encodeCurrentProjectDto(
  dto: ProjectDto,
  options: { compression?: boolean } = {},
): Promise<Blob> {
  try {
    return encodeProjectSnapshot(snapshotFromDto(dto).project, options);
  } catch (error) {
    return Promise.reject(error);
  }
}

function validateMetadata(
  data: ProjectSnapshotData,
  allowTemplateRoot = false,
): ProjectSnapshotData {
  // Validate metadata and edge schemas without rebuilding any node array. These
  // temporary empty arrays contain no state and are not retained representations.
  const envelope = ProjectEnvelopeDtoSchema.omit({ chips: true }).parse(data);
  if (!Array.isArray(data.chips) || data.chips.length > MAX_BLOCKS)
    throw new TypeError('Invalid current project chips');
  const ids = new Set<string>();
  const chips = data.chips.map((chip) => {
    if (
      !Array.isArray(chip.chipInstanceDtos) ||
      chip.chipInstanceDtos.length > MAX_BLOCKS
    )
      throw new TypeError('Invalid current project instances');
    const instance = (value: typeof chip.editingInstanceDto) => ({
      ...value,
      logicGraphDto: { ...value.logicGraphDto, nodes: [] },
    });
    const checked = ChipModelDtoSchema.parse({
      ...chip,
      chipInstanceDtos: chip.chipInstanceDtos.map(instance),
      editingInstanceDto: instance(chip.editingInstanceDto),
    });
    if (ids.has(checked.id)) throw new TypeError('Duplicate current project chip');
    ids.add(checked.id);
    return {
      ...checked,
      chipInstanceDtos: checked.chipInstanceDtos.map((value, i) => ({
        ...value,
        logicGraphDto: {
          ...value.logicGraphDto,
          nodes: chip.chipInstanceDtos[i].logicGraphDto.nodes,
        },
      })),
      editingInstanceDto: {
        ...checked.editingInstanceDto,
        logicGraphDto: {
          ...checked.editingInstanceDto.logicGraphDto,
          nodes: chip.editingInstanceDto.logicGraphDto.nodes,
        },
      },
    };
  });
  if (!ids.has(envelope.mainChipId))
    throw new TypeError('Missing current project main chip');
  const project = { ...envelope, chips };
  assertProjectDefinitionReferences(project, allowTemplateRoot);
  return project;
}

/** Read current bytes only. Unknown/old formats never silently enter a legacy path. */
export async function decodeProjectSnapshot(
  blob: Blob,
  report?: ProjectLoadReport,
  signal?: AbortSignal,
  limits?: ProjectDecodeLimits,
  options?: { allowTemplateRoot?: boolean },
): Promise<ProjectSnapshot> {
  if (!(blob instanceof Blob)) throw new TypeError('Invalid current project size');
  const original = blob;
  blob = await decompressProjectContainer(
    blob,
    HEADER_BYTES + MAX_MANIFEST_BYTES + MAX_BLOCK_BYTES,
    report,
    signal,
  );
  // Physical compressed input is reported by the inflater. Do not reset its
  // byte progress while reading the already inflated in-memory container.
  const reading = blob === original ? report : undefined;
  if (
    blob.size < HEADER_BYTES ||
    blob.size > HEADER_BYTES + MAX_MANIFEST_BYTES + MAX_BLOCK_BYTES
  )
    throw new TypeError('Invalid current project size');
  signal?.throwIfAborted();
  const header = await blob.slice(0, HEADER_BYTES).arrayBuffer();
  signal?.throwIfAborted();
  reading?.('reading', HEADER_BYTES, blob.size);
  const magic = new TextDecoder().decode(header.slice(0, 8));
  if (
    (magic !== MAGIC && !PREVIOUS_MAGICS.includes(magic)) ||
    new DataView(header).getUint32(12, true)
  )
    throw new TypeError('Unsupported current project format');
  if (limits?.currentVersionOnly && magic !== MAGIC)
    throw new TypeError('Unsupported hosted project version');
  const length = new DataView(header).getUint32(8, true);
  if (length > MAX_MANIFEST_BYTES || HEADER_BYTES + length > blob.size)
    throw new TypeError('Invalid current project manifest length');
  const manifest = JSON.parse(
    await blob.slice(HEADER_BYTES, HEADER_BYTES + length).text(),
  ) as Manifest;
  signal?.throwIfAborted();
  reading?.('reading', HEADER_BYTES + length, blob.size);
  if (
    manifest.version !== Number(magic.at(-1)) ||
    !Array.isArray(manifest.records) ||
    manifest.records.length > MAX_BLOCKS ||
    !Array.isArray(manifest.roots) ||
    manifest.roots.length > MAX_BLOCKS
  )
    throw new TypeError('Invalid current project manifest');
  const runtime = manifest.version >= 3 ? manifest.runtime : undefined;
  if (
    manifest.version >= 3 &&
    (!runtime ||
      typeof runtime !== 'object' ||
      Object.keys(runtime).length !== 2 ||
      !Array.isArray(runtime.roots) ||
      runtime.roots.length > MAX_BLOCKS)
  )
    throw new TypeError('Invalid packed runtime manifest');
  if (manifest.version === 2 && manifest.runtime !== undefined)
    throw new TypeError('Unexpected packed runtime in version 2');
  if (manifest.version >= 4)
    manifest.project = unpackTiles(manifest.project, manifest.tiles, limits);
  else if (manifest.tiles !== undefined)
    throw new TypeError('Unexpected packed tile metadata');
  if (manifest.version >= 5)
    manifest.project = unpackConnections(
      manifest.project,
      manifest.tiles!.strings,
      manifest.connections,
    );
  else if (manifest.connections !== undefined)
    throw new TypeError('Unexpected packed connection metadata');
  const combinedRoots = [...manifest.roots, ...(runtime?.roots ?? [])];
  validateSnapshotLayout({ roots: combinedRoots, records: manifest.records });
  let total = HEADER_BYTES + length;
  for (const record of manifest.records) {
    if (
      !record ||
      !Number.isSafeInteger(record.bytes) ||
      record.bytes < 0 ||
      record.bytes > 256 * 1024
    )
      throw new TypeError('Invalid current project block size');
    total += record.bytes;
  }
  if (total !== blob.size || total - HEADER_BYTES - length > MAX_BLOCK_BYTES)
    throw new TypeError('Truncated or oversized current project blocks');
  const scalarPaths =
    manifest.version >= 6 && manifest.metadataScalars !== undefined
      ? (unpackMetadataScalars(
          manifest.metadataScalars,
          createMetadataScalarRestorer(manifest.project),
          limits,
        ) ?? [])
      : restoreMetadataScalars(manifest.project, manifest.metadataScalars);
  const metadata = validateMetadata(manifest.project, options?.allowTemplateRoot);
  assertMetadataScalars(metadata, scalarPaths);
  const stateBudget = { members: 0, text: 0 };
  let project = mapStates(metadata, (state) => decodeStateValue(state, stateBudget));
  validateProjectSnapshotLayout(project, manifest.roots.length);
  if (runtime)
    validatePackedRuntimeLayout(
      project,
      runtime.graphs,
      runtime.roots.length,
      undefined,
      limits,
    );
  let offset = HEADER_BYTES + length;
  const records: SnapshotPacket['records'] = [];
  // Read small atoms together. Each block then owns its own buffer, so keeping
  // one graph never pins an unrelated batch or the whole project payload.
  for (let first = 0; first < manifest.records.length; ) {
    let end = first,
      bytes = 0;
    while (
      end < manifest.records.length &&
      bytes + manifest.records[end].bytes <= 1024 * 1024
    )
      bytes += manifest.records[end++].bytes;
    signal?.throwIfAborted();
    const batch = await blob.slice(offset, offset + bytes).arrayBuffer();
    signal?.throwIfAborted();
    let within = 0;
    for (let i = first; i < end; i++) {
      const record = manifest.records[i];
      records.push({
        buffer: batch.slice(within, within + record.bytes),
        dependencies: record.dependencies,
      });
      within += record.bytes;
    }
    offset += bytes;
    reading?.('reading', offset, blob.size);
    first = end;
  }
  reading?.('reading', blob.size, blob.size);
  signal?.throwIfAborted();
  report?.('decoding', 0, 1);
  const roots = SnapshotBlock.receive({ version: 1, roots: combinedRoots, records });
  if (runtime)
    project = await unpackRuntimeRecords(
      project,
      runtime.graphs,
      roots.slice(manifest.roots.length),
      report && ((completed, total) => report('decoding', completed, total)),
      signal,
      limits,
    );
  return receiveProjectSnapshotData(project, roots.slice(0, manifest.roots.length));
}

export async function decodeCurrentProjectDto(
  blob: Blob,
  report?: ProjectLoadReport,
  signal?: AbortSignal,
  limits?: ProjectDecodeLimits,
  options?: { allowTemplateRoot?: boolean },
): Promise<ProjectDto> {
  const snapshot = await decodeProjectSnapshot(blob, report, signal, limits, options);
  signal?.throwIfAborted();
  // DTO expansion is synchronous and opaque; do not report completion before it returns.
  const project = projectSnapshotDto(snapshot);
  signal?.throwIfAborted();
  report?.('decoding', 1, 1);
  return project;
}
